import { useState, useCallback } from "react";
import { useCurrentAccountId } from "@zapier/identity";
import { DEFAULT_VIEW_SETTINGS } from "@zapier/utils";
import { useDefaultViewMigration } from "./useDefaultViewMigration";
import { DefaultViewSelection, DefaultViewSelectionFilters } from "./types";

const STORAGE_KEY = (accountId: number | undefined) =>
  `${DEFAULT_VIEW_SETTINGS}-${accountId}`;

/**
 * Gets all stored default views from localStorage
 */
function getStoredDefaultViews(
  currentAccountId: number | undefined,
): DefaultViewSelection {
  if (globalThis.window === undefined) {
    return {};
  }

  const stored = localStorage.getItem(STORAGE_KEY(currentAccountId));
  if (!stored) {
    return {};
  }

  return JSON.parse(stored);
}

/**
 * Gets the stored default view for a specific pathname
 */
function getStoredDefaultViewForPathname(
  currentAccountId: number | undefined,
  pathname: keyof DefaultViewSelection,
): DefaultViewSelectionFilters | undefined {
  const allViews = getStoredDefaultViews(currentAccountId);
  return allViews[pathname];
}

/**
 * Saves a default view to localStorage for a specific pathname
 */
function saveDefaultViewToStorage(
  currentAccountId: number | undefined,
  pathname: keyof DefaultViewSelection,
  filters: DefaultViewSelectionFilters,
): void {
  const allViews = getStoredDefaultViews(currentAccountId);
  const updatedViews = {
    ...allViews,
    [pathname]: filters,
  };

  localStorage.setItem(
    STORAGE_KEY(currentAccountId),
    JSON.stringify(updatedViews),
  );
}

interface UseDefaultViewStorageResult {
  savedFiltersForPathname: DefaultViewSelectionFilters | undefined;
  saveFilters: (
    pathname: keyof DefaultViewSelection,
    filters: DefaultViewSelectionFilters,
  ) => void;
  loadFiltersForPathname: (
    pathname: keyof DefaultViewSelection,
  ) => DefaultViewSelectionFilters | undefined;
  hasStoredView: (pathname: keyof DefaultViewSelection) => boolean;
  isReady: boolean;
}

/**
 * Custom hook to manage default view storage
 * Handles localStorage operations and state management
 */
export function useDefaultViewStorage(): UseDefaultViewStorageResult {
  const currentAccountId = useCurrentAccountId();
  const [savedFiltersForPathname, setSavedFiltersForPathname] =
    useState<DefaultViewSelectionFilters>();

  // Run migration once on mount and get ready state
  const isReady = useDefaultViewMigration();

  const saveFilters = useCallback(
    (
      pathname: keyof DefaultViewSelection,
      filters: DefaultViewSelectionFilters,
    ) => {
      saveDefaultViewToStorage(currentAccountId, pathname, filters);
      setSavedFiltersForPathname(filters);
    },
    [currentAccountId],
  );

  const loadFiltersForPathname = useCallback(
    (pathname: keyof DefaultViewSelection) => {
      const savedFilters = getStoredDefaultViewForPathname(
        currentAccountId,
        pathname,
      );
      setSavedFiltersForPathname(savedFilters);
      return savedFilters;
    },
    [currentAccountId],
  );

  const hasStoredView = useCallback(
    (pathname: keyof DefaultViewSelection) => {
      return !!getStoredDefaultViews(currentAccountId)[pathname];
    },
    [currentAccountId],
  );

  return {
    savedFiltersForPathname,
    saveFilters,
    loadFiltersForPathname,
    hasStoredView,
    isReady,
  };
}
